<template>
  <div>
    <EquipmentEditHeader v-if="isAdmin" data-cy="vsd-search-view-edit-header" />

    <div v-else class="mb-3">
      <div class="flex align-items-center gap-4">
        <div v-if="isHps" class="flex align-items-center field-checkbox">
          <Checkbox
            v-model="enableFilter"
            input-id="enable-vsd-filter"
            binary
          />
          <label for="enable-vsd-filter">Filter and Sort Surface Drive</label>
        </div>
      </div>
    </div>

    <div
      v-if="noMotorForFilter"
      class="no-recommendation-message-container"
      data-cy="vsd-no-motor-message"
    >
      <div class="no-recommendation-message">
        <p class="no-recommendation-heading">
          No surface drive recommendations can be made.
        </p>
        <p class="no-recommendation-body">
          Please select a motor to see surface drive/VSD recommendations, or
          un-check
          <span class="no-recommendation-highlight">
            "Filter and Sort Surface Drive"
          </span>
          to view all options.
        </p>
      </div>
    </div>

    <div
      v-else-if="noVsdsWithFilter"
      class="no-recommendation-message-container"
      data-cy="vsd-no-results-message"
    >
      <div class="no-recommendation-message">
        <p class="no-recommendation-heading">
          No surface drives/VSDs match this configuration.
        </p>
        <p class="no-recommendation-body">
          If you'd like to select a VSD, please un-check
          <span class="no-recommendation-highlight">
            "Filter and Sort Surface Drive"
          </span>
          to view all available options.
        </p>
      </div>
    </div>

    <EquipmentTable
      v-else
      data-cy="vsd-search-view-equipment-table"
      @select="$emit('select', $event.data as Variable_Speed_Drives)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref, inject, watch } from "vue";
import { useVSDs } from "@/composables/equipment/vsd/vsd";
import EquipmentEditHeader from "@/components/equipment/EquipmentEditHeader.vue";
import EquipmentTable from "@/components/equipment/EquipmentTable.vue";
import type { Variable_Speed_Drives } from "gql/api";
import Checkbox from "primevue/checkbox";
import {
  equipmentFormKey,
  scenarioCalculationsKey,
  scenarioFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { Sizing_Pumping_Type_Enum } from "gql/api";

const props = defineProps<{
  isAdmin: boolean;
}>();

defineEmits<{
  (event: "select", data: Variable_Speed_Drives): void;
}>();

const vsdSearch = useVSDs(props.isAdmin);

const scenarioForm = !props.isAdmin ? inject(scenarioFormKey, null) : null;
const scenarioCalculations = !props.isAdmin
  ? inject(scenarioCalculationsKey, null)
  : null;
const equipmentForm = !props.isAdmin ? inject(equipmentFormKey, null) : null;

const isHps = computed(() => {
  if (!scenarioForm) return false;
  return scenarioForm.data.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps;
});

const enableFilter = ref<boolean>(isHps.value);

watch(
  isHps,
  value => {
    if (value && !enableFilter.value) {
      enableFilter.value = true;
    }
  },
  { immediate: false }
);

const isMotorSelected = computed(() => {
  if (!equipmentForm) return false;
  const eq = equipmentForm.data.value;
  return !!(eq.motor_id || eq.lower_motor_id);
});

const sortedVsds = computed(() => {
  const rows = vsdSearch.allRows.value;

  if (!rows) {
    return rows;
  }

  if (props.isAdmin || !isHps.value || !enableFilter.value) {
    return rows;
  }

  if (!isMotorSelected.value) {
    return [];
  }

  if (!scenarioCalculations) {
    return rows;
  }

  const surfaceDriveCalcs = scenarioCalculations.surfaceDriveCalculations;
  const requiredKva =
    surfaceDriveCalcs.requiredKVA60Hz.value ||
    surfaceDriveCalcs.requiredKVA.value;
  // Prefer catalog kVA <= 1.25× required (same as transformers); relax if sizes skip the band.
  const maxKvaPreferred = requiredKva * 1.25;

  if (!requiredKva) {
    return rows;
  }

  const collectPassing = (usePreferredMaxKva: boolean) => {
    const out: Array<{
      row: Variable_Speed_Drives;
      kva: number;
    }> = [];

    rows.forEach(row => {
      const kva = row.kva;

      if (kva === null || kva === undefined) {
        return;
      }

      const kvaOk =
        kva >= requiredKva &&
        (!usePreferredMaxKva || kva <= maxKvaPreferred);

      if (kvaOk) {
        out.push({ row, kva });
      }
    });

    return out;
  };

  let passing = collectPassing(true);
  if (passing.length === 0) {
    passing = collectPassing(false);
  }

  if (passing.length === 0) {
    return [];
  }

  // Smallest KVA that still meets the requirement is preferred.
  passing.sort((a, b) => a.kva - b.kva);

  return passing.map(entry => entry.row);
});

const sortedFilteredRows = computed(() => {
  const baseFiltered = vsdSearch.filteredRows.value;
  const sorted = sortedVsds.value;

  if (!baseFiltered || !sorted) {
    return sorted as typeof baseFiltered;
  }

  const filteredIds = new Set(baseFiltered.map(p => p.id));
  return sorted.filter(vsd => filteredIds.has(vsd.id));
});

const noMotorForFilter = computed(() => {
  const filterOn = enableFilter.value && isHps.value && !props.isAdmin;
  const hasLoadedRows = Array.isArray(vsdSearch.allRows.value);
  return filterOn && hasLoadedRows && !isMotorSelected.value;
});

const noVsdsWithFilter = computed(() => {
  const filterOn = enableFilter.value && isHps.value && !props.isAdmin;
  const count = sortedFilteredRows.value?.length ?? 0;
  const hasLoadedRows = Array.isArray(vsdSearch.allRows.value);
  return filterOn && hasLoadedRows && count === 0 && isMotorSelected.value;
});

const equipmentWithSortedVsds = {
  ...vsdSearch,
  allRows: sortedVsds,
  filteredRows: sortedFilteredRows
};

provide("equipment", equipmentWithSortedVsds);
</script>
